import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import * as AntdIcons from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useMenu } from '../../hooks/useData';

function Sidebar({ collapsed, onCloseMobileMenu, isStudioRoute = false }) {
  const { userInfo } = useAuth();
  const { menu, loading: menuLoading, error: menuError } = useMenu();
  const auth = userInfo || null;
  const location = useLocation();
  const navigate = useNavigate();
  const { code, os, studio: studioParam } = useParams();
  
  // Get studio from URL params if available, otherwise use auth.studio
  const studio = studioParam || auth?.studio;

  // Helper function to normalize path (remove trailing slash, ensure consistent format)
  const normalizePath = (path) => {
    if (!path || path === '#' || path === '/') return path;
    // Decode URL encoding to ensure consistent comparison
    // Need to decode each segment separately to handle spaces and special characters
    try {
      // Split by '/' and decode each segment, then join back
      const segments = path.split('/');
      const decodedSegments = segments.map(segment => {
        try {
          return decodeURIComponent(segment);
        } catch (e) {
          return segment;
        }
      });
      path = decodedSegments.join('/');
    } catch (e) {
      // If decode fails, use original path
    }
    return path.replace(/\/+$/, '') || '/';
  };

  // Get current selected key
  const getSelectedKey = () => {
    if (!menu) return [];
    const menuItems = code ? menu?.menu_app_detail?.values : menu?.menu_overview?.values;
    if (!menuItems) return [];

    const normalizedPathname = normalizePath(location.pathname);

    // First, check for exact matches with child menus (more specific paths first)
    for (let i of menuItems) {
      if (i.child && i.child.length > 0) {
        for (let child of i.child) {
          const childPath = normalizePath(
            child.url
              .replace(':code', code || '')
              .replace(':os', os || '')
              .replace(':studio', studio || '')
          );
          
          // Check exact match first
          if (normalizedPathname === childPath) {
            return [childPath];
          }
          // Also check if pathname starts with child path (for nested routes)
          if (childPath !== '#' && childPath !== '/' && normalizedPathname.startsWith(childPath + '/')) {
            return [childPath];
          }
        }
      }
    }

    // Then check for parent menu matches
    for (let i of menuItems) {
      const itemPath = normalizePath(
        i.url
          .replace(':code', code || '')
          .replace(':os', os || '')
          .replace(':studio', studio || '')
      );

      // Check exact match first
      if (normalizedPathname === itemPath) {
        return [itemPath];
      }
      // Then check if pathname starts with parent path
      if (itemPath !== '/' && normalizedPathname.startsWith(itemPath + '/')) {
        // Only return parent if it doesn't have children (or children don't match)
        if (!i.child || i.child.length === 0) {
          return [itemPath];
        }
      }
    }
    
    return [];
  };

  // Get open keys for submenus - only open the parent menu that contains the active child
  const getOpenKeys = () => {
    if (!menu) return [];
    const menuItems = code ? menu?.menu_app_detail?.values : menu?.menu_overview?.values;
    if (!menuItems) return [];

    // Find the parent menu that contains the currently active path
    for (let i of menuItems) {
      if (i.child && i.child.length > 0) {
        const itemPath = i.url
          .replace(':code', code || '')
          .replace(':os', os || '')
          .replace(':studio', studio || '');

        // Check if any child matches the current pathname exactly
        for (let child of i.child) {
          const childPath = child.url
            .replace(':code', code || '')
            .replace(':os', os || '')
            .replace(':studio', studio || '');
          
          if (location.pathname === childPath) {
            // Open the parent menu that contains this active child
            return [itemPath];
          }
        }
        
        // Also check if current pathname starts with parent path (for nested routes)
        if (itemPath !== '/' && location.pathname.startsWith(itemPath)) {
          // Check if any child path is part of current pathname
          for (let child of i.child) {
          const childPath = child.url
            .replace(':code', code || '')
            .replace(':os', os || '')
            .replace(':studio', studio || '');
            if (childPath !== '#' && location.pathname.startsWith(childPath)) {
              return [itemPath];
            }
          }
        }
      }
    }
    return [];
  };

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const isClickingChildRef = useRef(false);

  useEffect(() => {
    const newSelectedKeys = getSelectedKey();
    
    // Always update selectedKeys based on current location
    setSelectedKeys(newSelectedKeys);
    
    // Only update openKeys if not actively clicking a child menu
    // This prevents closing the parent menu when clicking a child
    if (!isClickingChildRef.current) {
      const newOpenKeys = getOpenKeys();
      setOpenKeys(newOpenKeys);
    } else {
      // If clicking child, ensure parent is open
      // Find parent key from menu data
      const menuItemsData = code ? menu?.menu_app_detail?.values : menu?.menu_overview?.values;
      if (newSelectedKeys.length > 0 && menuItemsData) {
        let parentKey = null;
        for (const item of menuItemsData) {
          if (item.child && item.child.length > 0) {
            const itemPath = item.url
              .replace(':code', code || '')
              .replace(':os', os || '')
              .replace(':studio', studio || '');
            for (const child of item.child) {
              const childPath = child.url
                .replace(':code', code || '')
                .replace(':os', os || '')
                .replace(':studio', studio || '');
              if (childPath === newSelectedKeys[0]) {
                parentKey = itemPath;
                break;
              }
            }
            if (parentKey) break;
          }
        }
        
        if (parentKey) {
          setOpenKeys([parentKey]);
        }
      }
      
      // Reset flag after navigation completes
      setTimeout(() => {
        isClickingChildRef.current = false;
      }, 200);
    }
  }, [location.pathname, menu, code, os, studio]);

  // Helper function to render icon (supports both Font Awesome and Ant Design icons)
  // 
  // Format trong database:
  // - Font Awesome: "fa-tablet-alt" hoặc "fa fa-sm fa-tablet-alt"
  // - Ant Design: "TabletOutlined" hoặc "antd:TabletOutlined"
  // 
  // Nếu icon bắt đầu bằng "antd:" hoặc không chứa "fa" và "fa-", sẽ coi là Ant Design icon
  const renderIcon = (iconString) => {
    if (!iconString) return null;

    // Check if it's Ant Design icon
    // Format: "TabletOutlined" hoặc "antd:TabletOutlined"
    const isAntdIcon = iconString.startsWith('antd:') || 
                      (!iconString.includes('fa ') && !iconString.startsWith('fa-'));
    
    if (isAntdIcon) {
      // Remove "antd:" prefix if exists
      const iconName = iconString.replace('antd:', '');
      // Get the icon component from Ant Design icons
      const IconComponent = AntdIcons[iconName];
      
      if (IconComponent) {
        return <IconComponent />;
      }
      // Fallback if icon not found
      console.warn(`Ant Design icon "${iconName}" not found`);
      return null;
    }
    
    // Font Awesome icon (format: "fa-tablet-alt" hoặc "fa fa-sm fa-tablet-alt")
    const faClass = iconString.startsWith('fa ') 
      ? iconString 
      : iconString.startsWith('fa-')
        ? `fa fa-sm ${iconString}`
        : `fa fa-sm fa-${iconString}`;
    
    return <i className={faClass} />;
  };

  // Convert menu items to Ant Design Menu format
  const convertMenuItems = (items) => {
    if (!items) return [];

    return items.map((item, index) => {
      const itemPath = normalizePath(
        item.url
          .replace(':code', code || '')
          .replace(':os', os || '')
          .replace(':studio', studio || '')
      );

      const isDisabled = (!item?.child && item.url === '#') || 
                        (item.child && item.child.length === 0 && item.url === '#');

      // Ensure unique key by combining with index if itemPath is '#'
      const uniqueKey = itemPath === '#' 
        ? `menu-item-${index}` 
        : itemPath;

      const menuItem = {
        key: uniqueKey,
        label: item.name,
        icon: renderIcon(item.icon),
        disabled: isDisabled,
      };

      if (item.child && item.child.length > 0) {
        menuItem.children = item.child.map((child, childIndex) => {
          const childPath = normalizePath(
            child.url
              .replace(':code', code || '')
              .replace(':os', os || '')
              .replace(':studio', studio || '')
          );

          const isChildDisabled = (!child.url || child.url === '#');
          
          // Ensure unique key by combining parent key with child index if child path is '#'
          const childUniqueKey = childPath === '#' 
            ? `${uniqueKey}-child-${childIndex}` 
            : childPath;

          return {
            key: childUniqueKey,
            label: child.name,
            icon: <i className="fa fa-sm fa-fw" style={{ visibility: 'hidden' }} />,
            disabled: isChildDisabled,
          };
        });
      }

      return menuItem;
    });
  };

  const menuItems = useMemo(() => {
    if (!menu) return [];
    const items = code ? menu?.menu_app_detail?.values : menu?.menu_overview?.values;
    return convertMenuItems(items || []);
  }, [menu, code, os, studio]);

  const handleMenuClick = ({ key }) => {
    // Check if the clicked key is a parent menu (has children)
    const isParentMenu = menuItems.some(item => item.key === key && item.children && item.children.length > 0);
    
    if (!isParentMenu) {
      // Check if it's a child menu - find its parent
      let parentKey = null;
      for (const item of menuItems) {
        if (item.children && item.children.length > 0) {
          const foundChild = item.children.find(child => child.key === key);
          if (foundChild) {
            parentKey = item.key;
            break;
          }
        }
      }
      
      // If it's a child menu, ensure parent is open FIRST
      if (parentKey) {
        // Set flag to track that we're clicking a child menu
        isClickingChildRef.current = true;
        // Set openKeys immediately to ensure parent menu is opened and stays open
        // This will open the parent menu if it's currently closed
        setOpenKeys(prev => {
          if (!prev.includes(parentKey)) {
            // If parent is not open, open it (accordion mode - only one at a time)
            return [parentKey];
          }
          // If parent is already open, keep it open
          return prev;
        });
        // Set selected key immediately so menu con is active right away
        setSelectedKeys([key]);
      } else {
        // If it's a menu item without children, set selectedKeys immediately
        setSelectedKeys([key]);
      }
      
      // Navigate to the menu
      // After navigation, useEffect will run and update selectedKeys/openKeys based on location.pathname
      navigate(key);
      // Close mobile menu after navigation
      if (onCloseMobileMenu && window.innerWidth <= 768) {
        onCloseMobileMenu();
      }
    }
    // If it's a parent menu, let handleOpenChange handle it
  };

  const handleOpenChange = (keys) => {
    // Accordion mode: only allow one submenu to be open at a time
    // Find which keys are newly opened (exist in keys but not in current openKeys)
    const newlyOpenedKeys = keys.filter(key => !openKeys.includes(key));
    
    if (newlyOpenedKeys.length > 0) {
      // A new menu was opened, close all others and only keep the last newly opened one
      // Use the last one in case multiple menus are somehow triggered at once
      setOpenKeys([newlyOpenedKeys[newlyOpenedKeys.length - 1]]);
    } else {
      // A menu was closed (clicked again), allow it to close
      setOpenKeys(keys);
    }
  };

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: isStudioRoute ? 'transparent' : '#fff'
    }}>
      {code && (
        <div>
          <Menu
            mode="inline"
            theme={isStudioRoute ? 'dark' : 'light'}
            style={{ border: 'none', background: 'transparent' }}
            onClick={(e) => {
              handleMenuClick(e);
              // Close mobile menu after navigation
              if (onCloseMobileMenu && window.innerWidth <= 768) {
                onCloseMobileMenu();
              }
            }}
            selectedKeys={['/']}
          >
            <Menu.Item key="/" icon={<i className="fa fa-sm fa-arrow-left" />}>
              <span>All Apps</span>
            </Menu.Item>
          </Menu>
        </div>
      )}
      <Menu
        mode="inline"
        theme={isStudioRoute ? 'dark' : 'light'}
        inlineCollapsed={collapsed}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        multiple={false}
        style={{ 
          flex: 1,
          borderRight: 0,
          overflow: 'auto',
          background: 'transparent'
        }}
        items={menuItems}
        forceSubMenuRender={true}
      />
    </div>
  );
}

export default Sidebar;
